/**
 * Demo Mode Anonymizer Utility
 * 
 * Provides deterministic masking of sensitive data (names, addresses, phones, emails).
 * Deterministic means the same input (e.g., "John Doe") always produces the same
 * output (e.g., "Riverside Estate"), which prevents UI flickering during re-renders.
 */

const FIRST_NAMES = ['Aiden', 'Bella', 'Caleb', 'Daisy', 'Ethan', 'Fiona', 'Gavin', 'Hazel', 'Isaac', 'Jade', 'Kaleb', 'Luna', 'Mason', 'Nova', 'Owen', 'Piper', 'Quinn', 'Ruby', 'Silas', 'Thea']
const LAST_NAMES = ['Anderson', 'Baker', 'Clark', 'Davis', 'Evans', 'Foster', 'Garcia', 'Hill', 'Irwin', 'Jones', 'King', 'Lopez', 'Miller', 'Nelson', 'Ortiz', 'Perez', 'Quinn', 'Reed', 'Smith', 'Taylor']
const STREET_NAMES = ['Oak', 'Maple', 'Pine', 'Cedar', 'Willow', 'Elm', 'Birch', 'Walnut', 'Chestnut', 'Aspen', 'Hickory', 'Linden', 'Cypress', 'Magnolia', 'Sycamore', 'Juniper', 'Redwood', 'Sequoia', 'Spruce', 'Poplar']
const STREET_SUFFIXES = ['St', 'Ave', 'Dr', 'Ln', 'Ct', 'Blvd', 'Way', 'Pl', 'Rd']
const CITY_NAMES = ['Phoenix', 'Scottsdale', 'Mesa', 'Gilbert', 'Chandler', 'Tempe', 'Glendale', 'Peoria', 'Surprise', 'Paradise Valley']
const COMPANY_NAMES = ['Acme Corp', 'Global Logistics', 'Summit Group', 'Northstar Inc', 'Vanguard Solutions', 'Apex Industries', 'Starlight Enterprises', 'Horizon Ventures', 'Pacific Coast Partners', 'Desert Sky Mgmt']
const PROPERTY_MODIFIERS = ['Center', 'Plaza', 'Park', 'Estates', 'Highlands', 'Gardens', 'Commons', 'Square', 'Village', 'Crossing']

/**
 * Deterministic hash based on a string seed
 */
const getSeed = (str = '') => {
  if (!str) return 0
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash)
}

/**
 * Pick an item from an array deterministically
 */
const pick = (arr, seed) => arr[seed % arr.length]

export const maskName = (name) => {
  if (!name) return '-'
  const seed = getSeed(String(name))
  return `${pick(FIRST_NAMES, seed)} ${pick(LAST_NAMES, seed + 1)}`
}

export const maskCompany = (company) => {
  if (!company) return 'No Company'
  const seed = getSeed(String(company))
  return pick(COMPANY_NAMES, seed)
}

export const maskAddress = (address) => {
  if (!address) return ''
  const seed = getSeed(String(address))
  const num = (seed % 9000) + 100
  const street = pick(STREET_NAMES, seed)
  const suffix = pick(STREET_SUFFIXES, seed + 1)
  return `${num} ${street} ${suffix}`
}

export const maskPropertyName = (name) => {
  if (!name) return 'Unnamed Site'
  const seed = getSeed(String(name))
  const base = pick(STREET_NAMES, seed)
  const modifier = pick(PROPERTY_MODIFIERS, seed + 1)
  return `${base} ${modifier}`
}

export const maskPhone = (seedStr) => {
  const seed = getSeed(String(seedStr))
  const part1 = (seed % 800) + 100
  const part2 = (seed % 900) + 100
  const part3 = (seed % 9000) + 1000
  return `(${part1}) ${part2}-${part3}`
}

export const maskEmail = (seedStr) => {
  const seed = getSeed(String(seedStr))
  const user = pick(FIRST_NAMES, seed).toLowerCase()
  const domain = pick(COMPANY_NAMES, seed).replace(/\s+/g, '').toLowerCase()
  return `${user}@${domain}.com`
}

/**
 * Main function to anonymize a Property/Row object
 */
export const anonymizeProperty = (property) => {
  if (!property) return null
  
  const pId = property.propertyId || property.PropertyID || 'anon'
  
  const anonymized = {
    ...property,
    // Identification
    name: maskPropertyName(property.name || property.PropertyName),
    PropertyName: maskPropertyName(property.PropertyName || property.name),
    
    // Address
    address1: maskAddress(property.address1 || property.PropertyAddressLine1),
    PropertyAddressLine1: maskAddress(property.PropertyAddressLine1 || property.address1),
    address2: pick(CITY_NAMES, getSeed(String(pId))),
    PropertyAddressLine2: pick(CITY_NAMES, getSeed(String(pId))),
    
    // Company
    companyName: maskCompany(property.companyName || property.CompanyName),
    CompanyName: maskCompany(property.CompanyName || property.companyName),
    companyLabel: property.companyId ? `${maskCompany(property.companyName)} #${property.companyId}` : 'No company',
    
    // People
    primaryCrewLeader: property.primaryCrewLeader ? {
      ...property.primaryCrewLeader,
      name: maskName(property.primaryCrewLeader.name)
    } : null,
    
    crewName: property.crewName ? maskName(property.crewName) : '-',
    
    // Contacts (if any)
    contacts: (property.contacts || []).map(c => ({
      ...c,
      name: maskName(c.name || c.ContactName),
      ContactName: maskName(c.ContactName || c.name),
      phone: maskPhone(c.phone || c.ContactPhone || c.MobilePhone),
      email: maskEmail(c.email || c.ContactEmail)
    }))
  }

  return anonymized
}

/**
 * Anonymize a list of Visit/History objects
 */
export const anonymizeVisits = (visits) => {
  if (!Array.isArray(visits)) return []
  return visits.map(v => ({
    ...v,
    crewLeaderName: v.crewLeaderName ? maskName(v.crewLeaderName) : v.crewLeaderName,
    CrewLeaderName: v.CrewLeaderName ? maskName(v.CrewLeaderName) : v.CrewLeaderName,
    propertyName: v.propertyName ? maskPropertyName(v.propertyName) : v.propertyName,
    PropertyName: v.PropertyName ? maskPropertyName(v.PropertyName) : v.PropertyName,
    contactName: v.contactName ? maskName(v.contactName) : v.contactName,
    ContactName: v.ContactName ? maskName(v.ContactName) : v.ContactName
  }))
}

/**
 * Anonymize a list of Issues
 */
export const anonymizeIssues = (issues) => {
  if (!Array.isArray(issues)) return []
  return issues.map(i => ({
    ...i,
    PropertyName: i.PropertyName ? maskPropertyName(i.PropertyName) : i.PropertyName,
    Description: 'Detailed description of the reported issue (masked for demo).',
    Resolution: i.Resolution ? 'Masked resolution steps.' : i.Resolution,
    AssignedToContactName: i.AssignedToContactName ? maskName(i.AssignedToContactName) : i.AssignedToContactName,
    CreatedByContactName: i.CreatedByContactName ? maskName(i.CreatedByContactName) : i.CreatedByContactName
  }))
}

/**
 * Anonymize a list of Opportunities
 */
export const anonymizeOpportunities = (opps) => {
  if (!Array.isArray(opps)) return []
  return opps.map(o => ({
    ...o,
    OpportunityNumber: `OPP-${(getSeed(o.OpportunityNumber) % 9000) + 1000}`,
    OpportunityName: `${pick(STREET_NAMES, getSeed(o.OpportunityName))} Service`,
    ContactName: o.ContactName ? maskName(o.ContactName) : o.ContactName,
    PropertyName: o.PropertyName ? maskPropertyName(o.PropertyName) : o.PropertyName
  }))
}
